
import React, { useEffect, useState, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useZmanimCache } from "@/hooks/useZmanimCache";
import { ZmanimData, UserLocation, TimeItem, HADERA_COORDINATES } from "@/types/time";
import { Button } from "@/components/ui/button";
import { List, Grid } from "lucide-react";
import DisplayCard from "@/components/shared/DisplayCard";

// Helper function to sanitize any string values by removing debug attributes
const sanitizeString = (text: string): string => {
  if (typeof text !== 'string') return text;
  return text.replace(/data-lov-id="[^"]*"|data-lov-name="[^"]*"|data-component-path="[^"]*"|data-component-line="[^"]*"|data-component-file="[^"]*"|data-component-name="[^"]*"|data-component-content="[^"]*"/g, '');
};

// Helper function to format time in 24-hour format
const formatTimeDisplay = (time: string): string => {
  if (!time) return "00:00";
  
  // If already in 24-hour format (HH:mm), return as is
  if (/^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time)) {
    return time;
  }
  
  // Try to parse and convert to 24-hour format
  try {
    const [hours, minutes] = time.split(':').map(Number);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  } catch {
    return "00:00";
  }
};

// Helper function to calculate relative time based on reference point
const calculateRelativeTime = (referenceTime: string, offsetMinutes: number): string => {
  if (!referenceTime) return "00:00";
  
  try {
    const [hours, minutes] = referenceTime.split(':').map(Number);
    const totalMinutes = hours * 60 + minutes + offsetMinutes;
    
    const newHours = Math.floor(totalMinutes / 60) % 24;
    const newMinutes = totalMinutes % 60;
    
    return `${newHours.toString().padStart(2, '0')}:${Math.abs(newMinutes).toString().padStart(2, '0')}`;
  } catch {
    return "00:00";
  }
};

// Responsive font size utility
const clampFontSize = (min, preferred, max) => `clamp(${min}, ${preferred}, ${max})`;

interface TimesPageProps {
  content: {
    blockTitle?: string;
    times?: TimeItem[];
  };
  isHomePage?: boolean;
}

const TimesPage: React.FC<TimesPageProps> = ({ content, isHomePage = false }) => {
  const blockTitle = content?.blockTitle || "זמנים";
  const rawTimes = content?.times || [];
  const [displayTimes, setDisplayTimes] = useState<TimeItem[]>([]);
  const [zmanimData, setZmanimData] = useState<ZmanimData>({});
  const [isLoading, setIsLoading] = useState(false);
  const [userLocation, setUserLocation] = useState<UserLocation>(HADERA_COORDINATES);
  
  // Create a unique page ID for caching
  const pageId = useMemo(() => {
    // Create a hash from the content to uniquely identify this times page
    const contentHash = JSON.stringify(content || {})
      .split('')
      .reduce((a, b) => (((a << 5) - a) + b.charCodeAt(0))|0, 0)
      .toString(16);
    return `times-page-${contentHash}`;
  }, [content]);
  
  // Get cache functions
  const { 
    getCachedData, 
    setCachedData, 
    isCacheValid, 
    getEmergencyCachedData,
    getProcessedTimesCache,
    setProcessedTimesCache
  } = useZmanimCache();

  // Fetch user location from settings
  useEffect(() => {
    const fetchUserLocation = async () => {
      try {
        const { data, error } = await supabase
          .from('user_settings')
          .select('settings_value')
          .eq('settings_key', 'location')
          .single();
        
        if (error) {
          console.log("No user location found, using default Hadera coordinates:", error);
          return;
        }
        
        if (data?.settings_value) {
          const locationData = data.settings_value as { 
            geonameid?: string | number,
            name?: string,
            city?: string,
            country?: string,
            tzid?: string 
          };
          
          // Set the location data including city, country and timezone if available
          setUserLocation({
            ...HADERA_COORDINATES,
            name: locationData.name,
            city: locationData.city || HADERA_COORDINATES.city,
            country: locationData.country || HADERA_COORDINATES.country,
            tzid: locationData.tzid || HADERA_COORDINATES.tzid
          });
          
          if (locationData.geonameid) {
            // Fetch coordinates for the location
            const geoResponse = await fetch(
              `https://secure.geonames.org/getJSON?geonameId=${locationData.geonameid}&username=doravi`
            );
            
            if (geoResponse.ok) {
              const geoData = await geoResponse.json();
              setUserLocation(prevState => ({
                ...prevState,
                lat: parseFloat(geoData.lat),
                lng: parseFloat(geoData.lng)
              }));
              console.log("Using user location:", geoData.name, geoData.lat, geoData.lng);
            }
          }
        }
      } catch (error) {
        console.error("Error fetching user location:", error);
      }
    };
    
    fetchUserLocation();
  }, []);

  // Fetch zmanim data for relative time calculations
  useEffect(() => {
    const fetchZmanimData = async () => {
      // Check if we have processed times cache first - fastest option
      const cachedProcessedTimes = getProcessedTimesCache(pageId);
      if (cachedProcessedTimes && cachedProcessedTimes.length > 0) {
        console.log(`Using cached processed times for ${pageId}`);
        setDisplayTimes(cachedProcessedTimes);
        return;
      }
      
      // Check if we have any relative times - if not, we can just use raw times
      const hasRelativeTimes = rawTimes.some(t => t.is_relative);
      
      if (!hasRelativeTimes) {
        setDisplayTimes(rawTimes);
        // Cache the raw times too for next time
        setProcessedTimesCache(pageId, rawTimes);
        return;
      }
      
      // Check for valid zmanim cache
      if (isCacheValid(userLocation)) {
        const cachedData = getCachedData();
        if (cachedData) {
          console.log("Using cached zmanim data");
          setZmanimData(cachedData.data);
          const processedTimes = processDisplayTimes(cachedData.data);
          setDisplayTimes(processedTimes);
          // Cache the processed times
          setProcessedTimesCache(pageId, processedTimes);
          return;
        }
      }
      
      // No valid cache, need to fetch data - but don't show loading on homepage
      setIsLoading(!isHomePage);
      
      try {
        // First try to use the city/country method if available
        if (userLocation.city && userLocation.country) {
          console.log("Fetching shabbat times by city:", userLocation.city, userLocation.country);
          
          // Fetch shabbat times data from our new edge function with city/country
          const { data, error } = await supabase.functions.invoke('shabbat-times-by-city', {
            body: { 
              city: userLocation.city, 
              country: userLocation.country,
              tzid: userLocation.tzid || "Asia/Jerusalem"
            }
          });
          
          if (error) {
            console.error("Error from shabbat-times-by-city:", error);
            throw new Error(error.message);
          }
          
          if (data && data.shabbat && data.shabbat.length > 0) {
            console.log("Shabbat times data fetched by city:", data);
            
            // Convert the data format from Hebcal API format to our app's format
            const processedData: ZmanimData = {};
            
            // Process candles for shabbat_start
            const candlesItem = data.shabbat.find((item: any) => item.category === 'candles');
            if (candlesItem && candlesItem.title) {
              const match = candlesItem.title.match(/(\d{1,2}:\d{2})/);
              if (match && match[1]) {
                processedData.shabbat_start = match[1];
              }
            }
            
            // Process havdalah for shabbat_end
            const havdalahItem = data.shabbat.find((item: any) => item.category === 'havdalah');
            if (havdalahItem && havdalahItem.title) {
              const match = havdalahItem.title.match(/(\d{1,2}:\d{2})/);
              if (match && match[1]) {
                processedData.shabbat_end = match[1];
              }
            }
            
            // Fetch additional zmanim data from our original edge function for sunrise, sunset, etc.
            const today = new Date().toISOString().split('T')[0];
            
            try {
              const zmanimResponse = await supabase.functions.invoke('zmanim', {
                body: { 
                  date: today, 
                  lat: userLocation.lat, 
                  lng: userLocation.lng,
                  tzid: userLocation.tzid || "Asia/Jerusalem"
                }
              });
              
              if (!zmanimResponse.error && zmanimResponse.data) {
                // Merge zmanim data with shabbat times
                Object.assign(processedData, zmanimResponse.data);
              }
            } catch (error) {
              console.error("Error fetching zmanim data:", error);
              // Continue with partial data rather than failing completely
            }
            
            // Save to cache even if some data is missing
            setCachedData(processedData, userLocation);
            
            setZmanimData(processedData);
            
            // Process times to calculate actual display times
            const processedTimes = processDisplayTimes(processedData);
            setDisplayTimes(processedTimes);
            // Cache the processed times
            setProcessedTimesCache(pageId, processedTimes);
            setIsLoading(false);
            return;
          }
        }
      
        // Fallback to coordinates method if city method fails
        console.log("Falling back to coordinates method for zmanim");
        // Get current date in YYYY-MM-DD format
        const today = new Date().toISOString().split('T')[0];
        
        try {
          // Fetch shabbat times data from our edge function with user location
          const { data, error } = await supabase.functions.invoke('shabbat-times', {
            body: { 
              date: today, 
              lat: userLocation.lat, 
              lng: userLocation.lng,
              tzid: userLocation.tzid || "Asia/Jerusalem"
            }
          });
          
          if (error) {
            console.error("Error from shabbat-times:", error.message);
          } else if (data && data.times) {
            console.log("Shabbat times data fetched by coordinates:", data.times);
            
            // Save to cache
            setCachedData(data.times, userLocation);
            
            setZmanimData(data.times);
            
            // Process times to calculate actual display times
            const processedTimes = processDisplayTimes(data.times);
            setDisplayTimes(processedTimes);
            // Cache the processed times
            setProcessedTimesCache(pageId, processedTimes);
          } else {
            console.error("Invalid shabbat times data format:", data);
            setDisplayTimes(rawTimes); // Fallback to raw times
            setProcessedTimesCache(pageId, rawTimes);
          }
        } catch (error) {
          console.error("Error fetching shabbat times data:", error);
          
          // Try to use emergency cache data even if expired (but within 24 hours)
          const emergencyCache = getEmergencyCachedData();
          if (emergencyCache) {
            console.log("Using emergency cached data due to API failure");
            setZmanimData(emergencyCache.data);
            const processedTimes = processDisplayTimes(emergencyCache.data);
            setDisplayTimes(processedTimes);
            setProcessedTimesCache(pageId, processedTimes);
          } else {
            setDisplayTimes(rawTimes); // Fallback to raw times
            setProcessedTimesCache(pageId, rawTimes);
          }
        } finally {
          setIsLoading(false);
        }
      } catch (finalError) {
        console.error("Final error in fetchZmanimData:", finalError);
        setDisplayTimes(rawTimes);
        setProcessedTimesCache(pageId, rawTimes);
        setIsLoading(false);
      }
    };
    
    // Helper function to process times with the zmanim data
    const processDisplayTimes = (zmanimData: ZmanimData): TimeItem[] => {
      const processed = rawTimes.map(time => {
        if (!time.is_relative) {
          return time;
        }
        
        const refTime = zmanimData[time.relative_to || ""];
        if (!refTime) {
          return time; // If reference time not available, return original
        }
        
        const calculatedTime = calculateRelativeTime(refTime, time.offset_minutes || 0);
        return {
          ...time,
          calculatedTime: calculatedTime
        };
      });
      
      return processed;
    };
    
    fetchZmanimData();
  }, [rawTimes, userLocation, pageId, getCachedData, setCachedData, isCacheValid, getEmergencyCachedData, getProcessedTimesCache, setProcessedTimesCache, isHomePage]);

  // Helper function to check if a time value exists
  const hasValue = (value: string | undefined): boolean => {
    return !!value && value.trim() !== '';
  };

  // Calculate animation duration based on number of items
  const calculateAnimationDuration = (items: TimeItem[]) => {
    const baseDuration = 25; // Base duration in seconds
    const itemsPerDuration = 4; // Number of items per base duration
    return Math.max(baseDuration, Math.ceil(items.length / itemsPerDuration) * baseDuration);
  };

  // Show loading state first - but only if not on homepage
  if (isLoading) {
    return (
      <div className="flex flex-col h-full w-full items-center justify-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-3 border-t-3 border-[#8B1F35]"></div>
        <p className="mt-6 text-4xl font-bold text-shadow-md">טוען זמנים...</p>
      </div>
    );
  }

  // Only show no times message after loading completes
  if (displayTimes.length === 0) {
    return (
      <Alert className="mb-3 sm:mb-4 md:mb-6 bg-white/60 backdrop-blur-sm text-black p-4 sm:p-6 md:p-8">
        <AlertTitle className="text-8xl text-shadow-lg">אין זמנים להצגה</AlertTitle>
        <AlertDescription>
          <p className="text-7xl text-black/90 mt-4 text-shadow-md">
            לא הוגדרו זמנים עבור דף זה.
          </p>
        </AlertDescription>
      </Alert>
    );
  }

  const animationDuration = calculateAnimationDuration(displayTimes);
  const shouldAnimate = displayTimes.length > 2;

  return (
    <div className="max-w-full mx-auto px-4 py-4 max-h-screen overflow-hidden">
      <div className="rounded-3xl bg-white/15 backdrop-blur-sm shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden p-6 mb-6 relative border-2 border-transparent bg-gradient-to-br from-yellow-400/20 via-amber-500/15 to-yellow-600/20">
        {/* Soft fading gold border overlay for block title */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-yellow-400/30 via-transparent to-amber-500/30 pointer-events-none" />
        <div className="absolute inset-[1px] rounded-3xl bg-gradient-to-br from-white/80 to-white/60 backdrop-blur-sm" />
        
        <div className="relative z-10">
          <h2 className="font-black text-gray-900 text-center text-4xl lg:text-6xl xl:text-7xl leading-tight">{blockTitle}</h2>
          
          {/* Beautiful gold gradient divider matching the cards */}
          <div className="w-3/4 h-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-yellow-600 mt-3 mx-auto rounded-full shadow-sm" />
        </div>
      </div>
      
      {/* Responsive grid that ensures all cards fit on screen */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-h-[calc(100vh-200px)] overflow-hidden">
        {displayTimes.map((time) => (
          <DisplayCard
            key={time.id}
            title={time.title}
            time={time.show_time === false ? '' : (time.calculatedTime || time.time || '')}
            dividerPosition="afterTitle"
          />
        ))}
      </div>
    </div>
  );
};

export default TimesPage;
